import React, { useEffect, useRef, useState } from 'react';
import Chip, { ChipState } from './chip/Chip';

import './FilterGroup.scss';

/** the filter group state */
export type FilterGroupState = {
	/** the name (category) of the filter group */
	category: string;
	/** the state of the filters (Chips) in the filter group */
	filters: ChipState[];
};

/** props over the filter group state */
export type FilterGroupProps = FilterGroupState & {
	/** the display name (category) of the filter group */
	categoryTitle: string;
	/** the prefix for category aria label. eg.: "Filter by " */
	ariaLabelForCategory: string;
	/** The index of the category among the categories, only to identify the aria labeled by */
	categoryIndex: number;
	/**
	 * Callback function from parent (FilterDialog Component). Handles the change of one filter (chip) state.
	 * @param {string} category - the name of category, which contains the changed filter (chip).
	 * @param {string} label - the label of the changed filter (chip).
	 * @return {void}
	 */
	onFilterGroupChange: (category: string, label: string) => void;
};

/**
 * Filter Group Component
 * Displays a group (Category) of filters (Chips)
 * Forwards the filter changes
 * Handles the focus on the chips
 */
const FilterGroup: React.FC<FilterGroupProps> = ({
	category,
	filters,
	categoryTitle,
	ariaLabelForCategory,
	categoryIndex,
	onFilterGroupChange,
}) => {
	//Reference of the ChipContainer DOM HTML Element to handling focus
	const chipContainerEl = useRef<HTMLDivElement>(null);
	//Reference of the collections of Chip DOM HTML Element to handling focus
	const chipElList = useRef<HTMLDivElement[]>([]);

	//Handles the changes of the collections of Chip DOM HTML Element reference
	if (chipElList.current.length !== filters.length) {
		chipElList.current = Array(filters.length)
			.fill(null)
			.map((_, i) => chipElList.current[i] || null);
	}

	//Index of the currently focused chip. Value is -1 if there is no focused chip
	const [focusedChipIndex, setFocusedChipIndex] = useState<number>(-1);

	//Distance between chip HTML element lines. It is required to navigate with the up and down arrows.
	const chipContainerLineHeight = useRef<number>(0);

	//Sets the value of the chipContainerLineHeight after the first render
	useEffect(() => {
		const chipHeight = chipElList.current[0]?.offsetHeight;
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const chipContainerStyle = window.getComputedStyle(chipContainerEl.current);
		const chipContainerRowGap = +chipContainerStyle
			.getPropertyValue('row-gap')
			.slice(0, -2);
		chipContainerLineHeight.current = chipHeight + chipContainerRowGap;
	}, []);

	//Forwards the filter(chip) changes to the parent component
	const onChipChangeHandler = (label: string) => {
		onFilterGroupChange(category, label);
	};

	//Handles the navigation between chips with keyboard
	const onKeyDownOnChipContainer = (
		event: React.KeyboardEvent<HTMLDivElement>
	) => {
		if (focusedChipIndex != -1) {
			if (event.code === 'ArrowRight') {
				event.preventDefault();
				focusNext();
			} else if (event.code === 'ArrowLeft') {
				event.preventDefault();
				focusPrevious();
			} else if (event.code === 'ArrowUp') {
				event.preventDefault();
				focusAbove();
			} else if (event.code === 'ArrowDown') {
				event.preventDefault();
				focusBelow();
			} else if (event.code === 'Home') {
				event.preventDefault();
				focusFirst();
			} else if (event.code === 'End') {
				event.preventDefault();
				focusLast();
			}
		}
	};

	//Sets the focused chip index if sg. has been focused inside the chip container
	const onFocusinOnChipContainer = (
		event: React.FocusEvent<HTMLDivElement, Element>
	) => {
		const index = chipElList.current.indexOf(event.target as HTMLDivElement);
		setFocusedChipIndex(index);
	};

	//Focuses on the next chip
	const focusNext = () => {
		if (focusedChipIndex < chipElList.current.length - 1) {
			chipElList.current[focusedChipIndex + 1].focus();
		}
	};

	//Focuses on the previous chip
	const focusPrevious = () => {
		if (focusedChipIndex > 0) {
			chipElList.current[focusedChipIndex - 1].focus();
		}
	};

	//Focuses on the first chip in the filter group
	const focusFirst = () => {
		chipElList.current[0].focus();
	};

	//Focuses on the last chip in the filter group
	const focusLast = () => {
		chipElList.current[chipElList.current.length - 1].focus();
	};

	//Focuses on the closest element in the previous row
	//If there is no previous row (the currently focused chip is in the firs row), it focuses on the first chip
	const focusAbove = () => {
		if (focusedChipIndex !== 0) {
			const focusedChip: HTMLDivElement = chipElList.current[focusedChipIndex];
			const center = getCenterOfElement(focusedChip);
			center.y -= chipContainerLineHeight.current;

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			if (center.y < chipContainerEl.current.getBoundingClientRect().top) {
				focusFirst();
				return;
			}

			findClosestElementInLine(center.x, center.y, chipElList.current)?.focus();
		}
	};

	//Focuses on the closest element in the next row
	//If there is no next row (the currently focused chip is in the last row), it focuses on the last chip
	const focusBelow = () => {
		if (focusedChipIndex !== chipElList.current.length - 1) {
			const focusedChip: HTMLDivElement = chipElList.current[focusedChipIndex];
			const center = getCenterOfElement(focusedChip);
			center.y += chipContainerLineHeight.current;

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			if (center.y > chipContainerEl.current.getBoundingClientRect().bottom) {
				focusLast();
				return;
			}

			findClosestElementInLine(center.x, center.y, chipElList.current)?.focus();
		}
	};

	return (
		<div className="blk-ac-filter-group">
			<h3
				className="filter-group-category"
				id={`filter-group-category-${categoryIndex || 0}`}
			>
				{categoryTitle || category}
			</h3>
			<div
				role="toolbar"
				aria-labelledby={`filter-group-category-${categoryIndex || 0}`}
			>
				<div
					className="chips-container"
					role="group"
					aria-label={`${ariaLabelForCategory} ${categoryTitle || category}`}
					onKeyDown={(ev) => onKeyDownOnChipContainer(ev)}
					onFocus={(ev) => onFocusinOnChipContainer(ev)}
					ref={chipContainerEl}
				>
					{filters.map((filter, i) => (
						<Chip
							key={filter.label}
							label={filter.label}
							aria-label={filter.label}
							tabIndex={i ? -1 : 0}
							checked={filter.checked}
							onChange={onChipChangeHandler}
							ref={(el: HTMLDivElement) => (chipElList.current[i] = el)}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

/**
 * Returns the center coordinates of the given HTML Div element.
 * @param {HTMLDivElement} element - the HTML Div element.
 * @return {{ x: number; y: number }} - the x, y coordinates of the HTML Div element.
 */
const getCenterOfElement = (
	element: HTMLDivElement
): { x: number; y: number } => {
	const box = element.getBoundingClientRect();
	const x = (box.left + box.right) / 2;
	const y = (box.top + box.bottom) / 2;
	return { x, y };
};

/**
 * Returns an HTML element from the given HTML element list
 * that is closest to the given x y coordinates and is in one line with the given y coordinate.
 * Returns 'undefined' if there is no HTML element in one line with the given y coordinate.
 * @param {number} x - the x coordinate.
 * @param {number} y - the y coordinate.
 * @param {HTMLDivElement[]} elements - the list of the HTML Div element.
 * @return {HTMLDivElement | undefined} - the HTML Div element.
 */
const findClosestElementInLine = (
	x: number,
	y: number,
	elements: HTMLDivElement[]
): HTMLDivElement | undefined => {
	let closest = undefined;
	let closestDist = Number.MAX_VALUE;
	for (let i = 0; i < elements.length; i++) {
		const box = elements[i].getBoundingClientRect();
		//returns the HTML element if the bounding rectangle of the element includes the given coordinates.
		if (box.top < y && box.bottom > y && box.left < x && box.right > x) {
			return elements[i];
		}
		//continue if the HTML element is not in one line with the given coordinates.
		if (box.top > y || box.bottom < y) {
			continue;
		}
		const xDist = Math.min(Math.abs(box.left - x), Math.abs(box.right - x));
		if (!closest || closestDist > xDist) {
			closestDist = xDist;
			closest = elements[i];
		}
	}
	return closest;
};

export default FilterGroup;
